import {createGlobalState} from '@vueuse/core'
import {useSocketStore} from '@toolify/client/src/stores/SocketStore/useSocketStore'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {
  ILeaveResourceEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ILeaveResourceEmitPayload'
import {watch} from 'vue'
import {$ref, $$} from '@vue-macros/reactivity-transform/macros'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {isViewAsActive} from '@toolify/client/src/stores/ViewAsStore/functions/isViewAsActive'

interface IConnectedSocketResourceStateItem {
  resourceId: string|null
  resourceType: ResourceType
  ids: symbol[]
  isConnected: boolean
}

export const useGlobalSocketResourceState = createGlobalState(() => {
  const socketStore = useSocketStore()

  let connectedSocketResourceStateItems = $ref<IConnectedSocketResourceStateItem[]>([])

  function findConnectedSocketResourceStateItem(resourceType: ResourceType, resourceId: string|null) {
    return connectedSocketResourceStateItems.find(item => {
      return item.resourceId === resourceId && item.resourceType === resourceType
    })
  }

  function joinResource(id: symbol, resourceType: ResourceType, resourceId: string|null) {
    const connectedSocketResourceStateItem = findConnectedSocketResourceStateItem(resourceType, resourceId)
    if(!connectedSocketResourceStateItem) {
      connectedSocketResourceStateItems = connectedSocketResourceStateItems.concat([
        {
          resourceId,
          resourceType,
          ids: [id],
          isConnected: false,
        },
      ])
    } else {
      connectedSocketResourceStateItems = connectedSocketResourceStateItems.map(item => {
        if(item.resourceId === resourceId && item.resourceType === resourceType) {
          return {
            ...item,
            ids: item.ids.concat([id]),
          }
        }
        return item
      })
    }
    handleSocketResourceStateItems()
  }

  function leaveResource(id: symbol, resourceType: ResourceType, resourceId: string|null) {
    const connectedSocketResourceStateItem = findConnectedSocketResourceStateItem(resourceType, resourceId)
    if(!connectedSocketResourceStateItem) {
      return
    }
    connectedSocketResourceStateItems = connectedSocketResourceStateItems.map(item => {
      if(item.resourceId === resourceId && item.resourceType === resourceType) {
        return {
          ...item,
          ids: item.ids.filter(itemId => {
            return itemId !== id
          }),
        }
      }
      return item
    })
    handleSocketResourceStateItems()
  }

  function handleSocketResourceStateItems() {
    if(!socketStore.isConnected || isViewAsActive()) {
      return
    }
    const connectionsWithNoIds = connectedSocketResourceStateItems.filter(item => {
      return !item.ids.length
    })
    for(const connection of connectionsWithNoIds) {
      socketStore.socket.emit(GlobalSocketEventType.LeaveResource, {
        resourceType: connection.resourceType,
        resourceId: connection.resourceId,
      } as ILeaveResourceEmitPayload)
    }
    connectedSocketResourceStateItems = connectedSocketResourceStateItems.filter(item => {
      return item.ids.length
    })
    const unconnectedConnectionsWithIds = connectedSocketResourceStateItems.filter(item => {
      return item.ids.length && !item.isConnected
    })
    for(const connection of unconnectedConnectionsWithIds) {
      socketStore.socket.emit(GlobalSocketEventType.JoinResource, {
        resourceType: connection.resourceType,
        resourceId: connection.resourceId,
      })
    }
    connectedSocketResourceStateItems = connectedSocketResourceStateItems.map(item => {
      const foundUnconnectedConnection = unconnectedConnectionsWithIds.find(unconnectedConnection => {
        return unconnectedConnection.resourceType === item.resourceType && unconnectedConnection.resourceId === item.resourceId
      })
      if(foundUnconnectedConnection) {
        return {
          ...item,
          isConnected: true,
        }
      } else {
        return item
      }
    })
  }

  watch(() => {
    return socketStore.isConnected
  }, (value) => {
    if(value) {
      handleSocketResourceStateItems()
    } else {
      connectedSocketResourceStateItems = connectedSocketResourceStateItems.map(item => {
        return {
          ...item,
          isConnected: false,
        }
      })
    }
  })

  return $$({
    joinResource,
    leaveResource,
  })
})
